import { getTheme, IIconProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIconButton, IPSpinButton, IPText } from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import {
  CallOutRowStyle,
  CircleRingIcon,
  SkypeCircleCheckIcon,
  StatusErrorFullIcon,
} from 'components/Checklist/CheckListStyles';
import { IEvidenceTaskLinkItem } from 'components/AuditCriteria/EvidenceTaskLinks';
import EntityTagPicker from 'components/Pickers/EntityTagPicker';
import LocalizedDatePicker from 'components/Pickers/LocalizedDatePicker';
import RichTextEditor from 'components/Text/RichTextEditor';
import { globalKPIDecimal18c8 } from 'globalConstants';
import { getEntity } from 'globalFunctions';
import { getGlobalTextFieldDisabled, globalStackTokensGapSmall } from 'globalStyles';
import Asset from 'models/asset/asset';
import Control from 'models/control';
import Entity, { EntityTypes } from 'models/entity';
import KPI, { KPITypes } from 'models/kpi/kpi';
import KPIData from 'models/kpi/kpiData';
import Objective from 'models/objective/objective';
import Process from 'models/process/process';
import ResourceLink from 'models/resourceLink';
import Risk from 'models/risk';
import StringValue from 'models/stringValue/stringValue';
import Task from 'models/tasks/task';
import TaskTypeFormElement, { TaskTypeFieldType } from 'models/tasks/taskTypeFormElement';
import Theme from 'models/theme';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetAssets } from 'services/Api/assetService';
import { apiGetControls } from 'services/Api/controlService';
import { apiGetObjectives } from 'services/Api/objectiveService';
import { apiGetProcesses } from 'services/Api/processService';
import { apiGetRisks } from 'services/Api/riskService';
import { apiGetThemes } from 'services/Api/themeService';
import { apiRequest } from 'services/Auth/authConfig';
import { addDateDays, getDateDiffDays } from 'utils/datetime';
import { clearHtmlValue, getHtmlJSX } from 'utils/html';
import { validateMaxValue, validateMinValue } from '../../KPI/KPIHelpers';
import {
  SingleFormElementAttachmentsField,
  SingleFormElementInfoField,
  SingleFormElementInfoKPI,
  SingleFormElementWrapper,
} from './SingleFormElementWrappers';

export interface ISingleFormElementProps {
  field: TaskTypeFormElement;
  task?: Task;
  links: ResourceLink[];
  // The evidence tasks bound to a one-time check, keyed by LOWER-CASED criterionKey (the binding rows
  // are not case-normalised server-side). A check can hold several tasks, so the wrapper renders a
  // list. The audit execution tasks pass both; the criteria editor passes only the map, for the
  // one-time badge, and leaves the links to the criterion editor.
  evidenceTasksByCriterionKey?: Map<string, IEvidenceTaskLinkItem[]>;
  // Further pills of a criterion card, keyed by LOWER-CASED criterionKey: the criteria editor's "Answered by" and the
  // latest AI outcome (PLAN-075).
  criterionBadgesByKey?: Map<string, string[]>;
  onOpenEvidenceTask?: (taskId: number) => void;
  onUpdateValue: (kpi: KPI, data: KPIData) => void;
  onUpdateKPI: (kpi: KPI) => void;
  onSetKPI: (kpi: KPI) => void;
  onUpdateTaskForForm?: (task: Task) => void;
  addLinks: (links: ResourceLink[]) => void;
  setLinks: (links: ResourceLink[]) => void;
  removeLink: (link: ResourceLink) => void;
  onUpdateField?: (field: TaskTypeFormElement) => void;
  readonly: boolean;
  allowEdit: boolean;
  compact: boolean;
  hideAttachments?: boolean;
  children?: ReactNode;
  onEdit?: (field: TaskTypeFormElement) => void;
  // Opens the audit criterion editor for a projected criterion card. Only the hosts that manage
  // criteria pass it (the procedure form editor, the criteria editor); everywhere else a criterion
  // card stays locked.
  onEditCriterion?: (field: TaskTypeFormElement) => void;
  // Deleting and reordering a criterion card go to the entity's criteria SET, never to the form:
  // the form only shows where the projection put the question. Passed by the same hosts as
  // onEditCriterion.
  onRemoveCriterion?: (field: TaskTypeFormElement) => void;
  onMoveCriterionUp?: (field: TaskTypeFormElement) => void;
  onMoveCriterionDown?: (field: TaskTypeFormElement) => void;
  onRemove?: (field: TaskTypeFormElement) => void;
  onMoveUp?: (field: TaskTypeFormElement) => void;
  onMoveDown?: (field: TaskTypeFormElement) => void;
  // All KPIs, used by the expression editor to resolve referenced field names.
  kpis?: KPI[];
  // Design-time form preview (Fields library). Computed fields then show the read-only formula
  // editor for insight instead of an empty result box, and empty inputs show guidance placeholders.
  designTime?: boolean;
  // The field whose editor is open. The wrapper keeps the hover look on it, with the accent border,
  // while the editor is open.
  selected?: boolean;
  // Design-time only: called when a referenced field (operand) is clicked inside a computed field's
  // read-only formula preview, to open that operand's KPIPreviewModal ("drill in" to inspect an
  // ingredient of the formula). See the designTime branch in getSingleElement below.
}

//
// Type switcher
//
const SingleFormElement = (props: ISingleFormElementProps) => {
  const appContext = useContext(AppContext);

  try {
    if (props.field.kpi?.data.length === 0) {
      return null;
    }

    const getSingleElement = (): JSX.Element | null => {
      const newProps = { ...props };
      // Design-time preview shows the end-user experience: render inputs as enabled controls with
      // guidance placeholders even on managed forms, so nothing renders as an empty or greyed-out
      // box. Only where the host actually offers editing (allowEdit): a locked design-time surface -
      // an archived form, or one this user may not change - keeps the read-only rendering it was
      // handed, or the builder would invite answers into a form nobody can change. Answer time (a
      // task, an audit) never passes designTime and is unaffected.
      if (props.designTime && props.allowEdit) newProps.readonly = false;
      if (props.field.kpi?.hasAutomatedEvidence) newProps.readonly = true;
      // A RETIRED criterion is history: it still shows the answer this task gave it, but it can never
      // be answered again. An ACTIVE check field is deliberately NOT locked here - what the control's
      // check set owns is the QUESTION (locked in the wrapper: no edit, remove or move), while the
      // ANSWER is the auditor's work and the whole point of projecting the check onto the form.
      if (props.field.retiredOn) newProps.readonly = true;
      // a criterion outside the audit's standards is not asked on this task; it only shows at all
      // when it carries an answer from before, and that answer is history too
      if (props.field.notApplicable) newProps.readonly = true;

      if (props.field.fieldType === TaskTypeFieldType.KPI && props.field.kpi) {
        switch (props.field.kpi.type) {
          case KPITypes.Text:
            return <SingleFormElementText {...newProps} />;
          case KPITypes.TextMultiline:
            return <SingleFormElementTextMultiline {...newProps} />;
          case KPITypes.Number:
            return <SingleFormElementNumber {...newProps} />;
          case KPITypes.Html:
            return <SingleFormElementHtml {...newProps} />;
          case KPITypes.SuccesError:
            return <SingleFormElementSuccessError {...newProps} />;
          case KPITypes.Choice:
            return <SingleFormElementChoice {...newProps} />;
          case KPITypes.Date:
            return <SingleFormElementDate {...newProps} />;
          case KPITypes.Expression:
            return <SingleFormElementExpression {...newProps} />;
          default:
            return null;
        }
      } else {
        switch (props.field.fieldType) {
          case TaskTypeFieldType.AttachmentDocument:
          case TaskTypeFieldType.AttachmentListItem:
          case TaskTypeFieldType.AttachmentPage:
          case TaskTypeFieldType.AttachmentURL:
            return <SingleFormElementAttachment {...newProps} />;
          case TaskTypeFieldType.ContextAsset:
          case TaskTypeFieldType.ContextControl:
          case TaskTypeFieldType.ContextObjective:
          case TaskTypeFieldType.ContextProcess:
          case TaskTypeFieldType.ContextRequirement:
          case TaskTypeFieldType.ContextRisk:
            return <SingleFormElementContext {...newProps} />;
          default:
            return null;
        }
      }
    };

    // The wrapper hosts everything AROUND the input - the attachments, the comment, the evidence
    // controls - and only sees the props it is handed, so the retired lock has to be applied to those
    // as well: a criterion that can no longer be answered must not take new evidence either.
    const wrapperProps = props.field.retiredOn || props.field.notApplicable ? { ...props, readonly: true } : props;

    return <SingleFormElementWrapper {...wrapperProps}>{getSingleElement()}</SingleFormElementWrapper>;
  } catch (err) {
    appContext.setError(err);

    return null;
  }
};

export default SingleFormElement;

//
// Text
//
export const SingleFormElementText = (props: ISingleFormElementProps) => {
  const { t } = useTranslation(['forms']);

  const getValue = (): string | undefined => {
    if (!props.field.kpi) return undefined;

    return props.field.kpi.data[0].resultText ?? '';
  };

  return (
    <IPStack>
      <SingleFormElementInfoKPI {...props} />
      <IPTextField
        readOnly={props.readonly}
        placeholder={props.readonly ? undefined : t('forms:Preview.TextPlaceholder')}
        value={getValue()}
        maxLength={props.field.kpi?.maxValue ? Number.parseInt(props.field.kpi?.maxValue) : 1024}
        onChange={(ev, newValue) => {
          if (!props.field.kpi) return;
          const newData = props.field.kpi.data[0];
          newData.resultText = newValue;
          props.onUpdateValue(props.field.kpi, newData);
        }}
      />
    </IPStack>
  );
};

//
// Text multiline
//
export const SingleFormElementTextMultiline = (props: ISingleFormElementProps) => {
  const { t } = useTranslation(['forms']);

  const getValue = (): string | undefined => {
    if (!props.field.kpi) return undefined;

    return props.field.kpi.data[0].resultText ?? '';
  };

  return (
    <IPStack>
      <SingleFormElementInfoKPI {...props} />
      <IPTextField
        readOnly={props.readonly}
        placeholder={props.readonly ? undefined : t('forms:Preview.TextPlaceholder')}
        multiline
        autoAdjustHeight
        style={{ resize: 'vertical', maxHeight: 200, overflowY: 'scroll' }}
        value={getValue()}
        maxLength={props.field.kpi?.maxValue ? Number.parseInt(props.field.kpi?.maxValue) : 1024}
        onChange={(ev, newValue) => {
          if (!props.field.kpi) return;
          const newData = props.field.kpi.data[0];
          newData.resultText = newValue;
          props.onUpdateValue(props.field.kpi, newData);
        }}
      />
    </IPStack>
  );
};

//
// Text HTML
//
export const SingleFormElementHtml = (props: ISingleFormElementProps) => {
  const { t } = useTranslation(['forms']);

  const getValue = (): string | undefined => {
    if (!props.field.kpi) return undefined;

    return props.field.kpi.data[0].resultText ?? '';
  };

  return (
    <IPStack>
      <SingleFormElementInfoKPI {...props} />
      <RichTextEditor
        directEdit={true}
        disableAutoFocus={true}
        disallowFullscreen={true}
        compactToolbar={props.compact}
        placeholder={props.readonly ? undefined : t('forms:Preview.TextPlaceholder')}
        html={getValue()}
        onChange={(newValue) => {
          if (!props.field.kpi) return;
          const newData = props.field.kpi.data[0];
          newData.resultText = newValue;
          props.onUpdateValue(props.field.kpi, newData);
        }}
        maxHeight={300}
        maxLength={props.field.kpi?.maxValue ? Number.parseInt(props.field.kpi?.maxValue) : 1024}
        readOnly={props.readonly}
        hideRemainingChars={props.field.kpi?.maxValue === undefined}
      />
    </IPStack>
  );
};

//
// Number
//
export const SingleFormElementNumber = (props: ISingleFormElementProps) => {
  const getValue = (): string | undefined => {
    if (!props.field.kpi) return undefined;
    const currentResult = props.field.kpi.data[0].resultNumber;

    return currentResult !== undefined ? currentResult.toFixed(props.field.kpi.decimalCount) : '';
  };

  return (
    <IPStack>
      <SingleFormElementInfoKPI {...props} />
      <IPSpinButton
        disabled={props.readonly}
        value={getValue()}
        min={props.field.kpi?.minValue ? Number.parseFloat(props.field.kpi?.minValue) : -globalKPIDecimal18c8}
        max={props.field.kpi?.maxValue ? Number.parseFloat(props.field.kpi?.maxValue) : globalKPIDecimal18c8}
        onIncrement={(newValue) => {
          if (!props.field.kpi) return;
          if (!newValue) newValue = '0';
          newValue = (Number.parseFloat(newValue) + 1).toFixed(props.field.kpi?.decimalCount);

          return validateMaxValue(props.field.kpi, props.field.kpi?.defValue, newValue);
        }}
        onDecrement={(newValue) => {
          if (!props.field.kpi) return;
          if (!newValue) newValue = '1';
          newValue = (Number.parseFloat(newValue) - 1).toFixed(props.field.kpi?.decimalCount);

          return validateMinValue(props.field.kpi, props.field.kpi?.defValue, newValue);
        }}
        onValidate={(newValue) => {
          //normally empty values are discarded as not valid
          //also return empty strings to reset the field
          return newValue;
        }}
        onChange={(ev, newValue) => {
          if (!props.field.kpi) return;
          newValue = validateMaxValue(props.field.kpi, props.field.kpi?.defValue, newValue);
          newValue = validateMinValue(props.field.kpi, props.field.kpi?.defValue, newValue);
          const newData = props.field.kpi?.data[0];
          const newVal = newValue ? Number.parseFloat(newValue) : undefined;
          if (newVal !== newData.resultNumber) {
            newData.resultNumber = newVal;
            props.onUpdateValue(props.field.kpi, newData);
          }
        }}
        styles={{ root: { width: 150 } }}
      ></IPSpinButton>
    </IPStack>
  );
};

//
// Success & Error
//
export enum SingleFormElementSuccessErrorState {
  Success = 1,
  Failed = 0,
}

const getStatusIcon = (state?: SingleFormElementSuccessErrorState): IIconProps => {
  switch (state) {
    case SingleFormElementSuccessErrorState.Success:
      return SkypeCircleCheckIcon;
    case SingleFormElementSuccessErrorState.Failed:
      return StatusErrorFullIcon;
    default:
      return CircleRingIcon;
  }
};

export const SingleFormElementSuccessError = (props: ISingleFormElementProps) => {
  const [showCallout, setShowCallout] = useState<boolean>(false);
  // Anchor the menu to the clicked button itself, not an id selector: the designer renders the same
  // field twice (form list and live preview) and an id would resolve to the first copy.
  const [calloutTarget, setCalloutTarget] = useState<HTMLElement | undefined>(undefined);
  const { t } = useTranslation(['translation', 'task']);

  const html = useMemo(() => clearHtmlValue(props.field.kpi?.defValue ?? '', true, true, true), [props.field.kpi]);

  const updateValue = (state?: SingleFormElementSuccessErrorState) => {
    if (!props.field.kpi) return;
    const newData = props.field.kpi.data[0];
    newData.resultNumber = state;
    props.onUpdateValue(props.field.kpi, newData);
    setShowCallout(false);
  };

  //
  // Main render
  //
  return (
    <IPStack>
      {/* Show label with KPI info when set, otherwise render KPI info behind text (def value) */}
      {props.field.kpi?.showLabel && <SingleFormElementInfoKPI {...props} />}
      <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="start">
        <IPIconButton
          disabled={props.readonly}
          onClick={(ev) => {
            setCalloutTarget(ev.currentTarget as HTMLElement);
            setShowCallout(true);
          }}
          iconProps={getStatusIcon(props.field.kpi?.data[0].resultNumber)}
        />
        <div style={{ borderWidth: '5px', paddingTop: 5 }}>{getHtmlJSX(html)}</div>
        {!props.field.kpi?.showLabel && <SingleFormElementInfoKPI {...props} />}
      </IPStack>
      {showCallout && calloutTarget && (
        <IPCallout
          styles={{
            root: {
              padding: '10px 12px',
            },
          }}
          setInitialFocus
          target={calloutTarget}
          onDismiss={() => {
            setShowCallout(false);
          }}
          beakWidth={16}
        >
          <IPStack>
            <IPStack
              verticalAlign="center"
              horizontal
              tokens={globalStackTokensGapSmall}
              onClick={() => {
                updateValue(SingleFormElementSuccessErrorState.Success);
              }}
              styles={CallOutRowStyle}
            >
              <IPIconButton iconProps={SkypeCircleCheckIcon} />
              <IPText>{t('task:CheckList.Success')}</IPText>
            </IPStack>
            <IPStack
              verticalAlign="center"
              horizontal
              tokens={globalStackTokensGapSmall}
              onClick={() => {
                updateValue(SingleFormElementSuccessErrorState.Failed);
              }}
              styles={CallOutRowStyle}
            >
              <IPIconButton iconProps={StatusErrorFullIcon} />
              <IPText>{t('task:CheckList.Failed')}</IPText>
            </IPStack>
            <IPStack
              horizontal
              verticalAlign="center"
              tokens={globalStackTokensGapSmall}
              onClick={() => {
                updateValue(undefined);
              }}
              styles={CallOutRowStyle}
            >
              <IPIconButton iconProps={CircleRingIcon} />
              <IPText> {t('task:CheckList.Todo')}</IPText>
            </IPStack>
          </IPStack>
        </IPCallout>
      )}
    </IPStack>
  );
};

//
// Choice
//
export const SingleFormElementChoice = (props: ISingleFormElementProps) => {
  const emptyKey = 1000000000; //maxIntVal + 1

  const getValue = (): string | undefined => {
    if (!props.field.kpi) return undefined;
    const currentResult = props.field.kpi.data[0].resultNumber;

    return currentResult !== undefined ? currentResult.toString() : emptyKey.toString();
  };

  const getOptions = (): IPSelectableOption[] => {
    if (!props.field.kpi?.choices) return [];

    const items: IPSelectableOption[] = [];
    if (!props.field.kpi.required && props.field.kpi.defValue === undefined) {
      items.push({
        key: emptyKey,
        text: '',
        data: undefined,
      });
    }

    items.push(
      ...props.field.kpi.choices.map((c) => {
        return {
          key: c.stringValueKey?.toString() ?? '-',
          text: `${c.stringValueKey} - ${c.value}`,
          data: c,
        };
      }),
    );

    return items;
  };

  return (
    <IPStack>
      <SingleFormElementInfoKPI {...props} />
      <IPDropdown
        options={getOptions()}
        disabled={props.readonly}
        selectedKey={getValue()}
        onChange={(ev, option) => {
          if (!props.field.kpi) return;
          const newData = props.field.kpi?.data[0];
          if (option?.data) {
            const val = option?.data as StringValue;
            newData.resultNumber = val.stringValueKey;
            newData.resultText = val.value;
          } else {
            //empty value selected
            newData.resultNumber = undefined;
            newData.resultText = undefined;
          }
          props.onUpdateValue(props.field.kpi, newData);
        }}
      />
    </IPStack>
  );
};

//
// Date
//
export const SingleFormElementDate = (props: ISingleFormElementProps) => {
  const { t } = useTranslation(['forms']);

  const getValue = (): Date | undefined => {
    return props.field.kpi?.data[0].resultDate;
  };

  const getMinVal = (): Date | undefined => {
    if (!props.field.kpi) return undefined;
    const kpi = props.field.kpi;
    if (!kpi.defValue && kpi.minValue) {
      return new Date(kpi.minValue);
    } else if (kpi.defValue && kpi.minValue) {
      const nrDays = getDateDiffDays(new Date(kpi.minValue), new Date(kpi.defValue));

      return addDateDays(new Date(), nrDays);
    } else {
      return undefined;
    }
  };

  const getMaxVal = (): Date | undefined => {
    if (!props.field.kpi) return undefined;
    const kpi = props.field.kpi;
    if (!kpi.defValue && kpi.maxValue) {
      return new Date(kpi.maxValue);
    } else if (kpi.defValue && kpi.maxValue) {
      const nrDays = getDateDiffDays(new Date(kpi.maxValue), new Date(kpi.defValue));

      return addDateDays(new Date(), nrDays);
    } else {
      return undefined;
    }
  };

  return (
    <IPStack>
      <SingleFormElementInfoKPI {...props} />
      <LocalizedDatePicker
        disabled={props.readonly}
        placeholder={props.readonly ? undefined : t('forms:Preview.DatePlaceholder')}
        value={getValue()}
        minDate={getMinVal()}
        maxDate={getMaxVal()}
        onDateChange={(newValue) => {
          if (!props.field.kpi) return;
          const newData = props.field.kpi.data[0];
          newData.resultDate = newValue;
          props.onUpdateValue(props.field.kpi, newData);
        }}
        allowEmptyValue={!props.field.kpi?.required}
      />
    </IPStack>
  );
};

//
// Expression
//
export const SingleFormElementExpression = (props: ISingleFormElementProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'kpis']);
  const [val, setVal] = useState<string>('');
  const theme = getTheme();

  useEffect(() => {
    const result = getValue();
    setVal(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.field, props.task]);

  const getValue = (): string => {
    const result = props.field.kpi?.data[0].resultNumber;
    if (result === undefined) {
      return '';
    } else if (isNaN(result)) {
      return t('kpis:Fields.InvalidExpression');
    } else {
      return result.toFixed(props.field.kpi?.decimalCount ?? 0);
    }
  };

  return (
    <IPStack>
      <SingleFormElementInfoKPI {...props} />
      <IPTextField styles={getGlobalTextFieldDisabled(appContext, theme, 150)} readOnly={true} value={val} />
    </IPStack>
  );
};

//
// Attachment
//
export const SingleFormElementAttachment = (props: ISingleFormElementProps) => {
  return (
    <IPStack>
      <SingleFormElementInfoField {...props} />
      <SingleFormElementAttachmentsField {...props} />
    </IPStack>
  );
};

//
// Context
//
export const SingleFormElementContext = (props: ISingleFormElementProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'forms']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [controls, setControls] = useState<Control[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [risks, setRisks] = useState<Risk[]>([]);
  const [processes, setProcesses] = useState<Process[]>([]);
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [type, setType] = useState<EntityTypes>(EntityTypes.NotSet);

  const defSpecificItemTagColor = '#339e80';

  useEffect(() => {
    const type = getEntityType();
    setType(type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.field]);

  useEffect(() => {
    loadData(type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const loadData = async (type: EntityTypes): Promise<Entity[]> => {
    if (type === EntityTypes.Risk) {
      return await loadRisks();
    } else if (type === EntityTypes.Objective) {
      return await loadObjectives();
    } else if (type === EntityTypes.Process) {
      return await loadProcesses();
    } else if (type === EntityTypes.Asset) {
      return await loadAssets();
    } else if (type === EntityTypes.Control) {
      return await loadControls();
    } else if (type === EntityTypes.Requirement) {
      return await loadThemes();
    } else {
      return [];
    }
  };

  const loadRisks = async (): Promise<Entity[]> => {
    try {
      setRisks([]);
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const riskCol = await apiGetRisks(accessToken, appContext);
      setRisks(riskCol.risks);

      return riskCol.risks.map((p) => getEntity(p));
    } catch (err) {
      appContext.setError(err);

      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const loadAssets = async (): Promise<Entity[]> => {
    try {
      setAssets([]);
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const assets = await apiGetAssets(accessToken, appContext.globalDataCache);
      setAssets(assets);

      return assets.map((p) => getEntity(p));
    } catch (err) {
      appContext.setError(err);

      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const loadControls = async (): Promise<Entity[]> => {
    try {
      setControls([]);
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const controls = await apiGetControls(accessToken, appContext.globalDataCache);
      setControls(controls);

      return controls.map((p) => getEntity(p));
    } catch (err) {
      appContext.setError(err);

      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const loadThemes = async (): Promise<Entity[]> => {
    try {
      setThemes([]);
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const themes = await apiGetThemes(accessToken, appContext.globalDataCache);
      setThemes(themes);

      return themes.map((p) => getEntity(p));
    } catch (err) {
      appContext.setError(err);

      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const loadProcesses = async (): Promise<Entity[]> => {
    try {
      setProcesses([]);
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const processes = await apiGetProcesses(accessToken, appContext.globalDataCache);
      setProcesses(processes);

      return processes.map((p) => getEntity(p));
    } catch (err) {
      appContext.setError(err);

      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const loadObjectives = async (): Promise<Entity[]> => {
    try {
      setObjectives([]);
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const objectives = await apiGetObjectives(accessToken, appContext.globalDataCache);
      setObjectives(objectives);

      return objectives.map((p) => getEntity(p));
    } catch (err) {
      appContext.setError(err);

      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getEntityType = (): EntityTypes => {
    switch (props.field.fieldType) {
      case TaskTypeFieldType.ContextAsset:
        return EntityTypes.Asset;
      case TaskTypeFieldType.ContextControl:
        return EntityTypes.Control;
      case TaskTypeFieldType.ContextObjective:
        return EntityTypes.Objective;
      case TaskTypeFieldType.ContextProcess:
        return EntityTypes.Process;
      case TaskTypeFieldType.ContextRequirement:
        return EntityTypes.Requirement;
      case TaskTypeFieldType.ContextRisk:
        return EntityTypes.Risk;
      default:
        return EntityTypes.Process;
    }
  };

  const getEntities = (): Entity[] => {
    switch (props.field.fieldType) {
      case TaskTypeFieldType.ContextAsset:
        return assets.map((a) => getEntity(a));
      case TaskTypeFieldType.ContextControl:
        return controls.map((a) => getEntity(a));
      case TaskTypeFieldType.ContextObjective:
        return objectives.map((a) => getEntity(a));
      case TaskTypeFieldType.ContextProcess:
        return processes.map((a) => getEntity(a));
      case TaskTypeFieldType.ContextRequirement:
        return themes.map((a) => getEntity(a));
      case TaskTypeFieldType.ContextRisk:
        return risks.map((a) => getEntity(a));
      default:
        return [];
    }
  };

  const getSelectedEntitiesFromTask = (): Entity[] => {
    if (!props.task) return [];
    const entities: Entity[] = [];

    switch (props.field.fieldType) {
      case TaskTypeFieldType.ContextAsset:
        props.task.assetIds.forEach((id) => {
          const asset = assets.find((a) => a.assetId === id);
          if (asset) entities.push(getEntity(asset));
        });
        break;
      case TaskTypeFieldType.ContextControl:
        props.task.controlIds.forEach((id) => {
          const control = controls.find((a) => a.controlId === id);
          if (control) entities.push(getEntity(control));
        });
        break;
      case TaskTypeFieldType.ContextObjective:
        props.task.objectiveIds.forEach((id) => {
          const objective = objectives.find((a) => a.objectiveId === id);
          if (objective) entities.push(getEntity(objective));
        });
        break;
      case TaskTypeFieldType.ContextProcess:
        props.task.processIds.forEach((id) => {
          const process = processes.find((a) => a.processId === id);
          if (process) entities.push(getEntity(process));
        });
        break;
      case TaskTypeFieldType.ContextRequirement:
        props.task.controlIds.forEach((id) => {
          const theme = themes.find((a) => a.themeId === id);
          if (theme) entities.push(getEntity(theme));
        });
        break;
      case TaskTypeFieldType.ContextRisk:
        props.task.riskIds.forEach((id) => {
          const risk = risks.find((a) => a.riskId === id);
          if (risk) entities.push(getEntity(risk));
        });
        break;
      default:
        return [];
    }

    return entities;
  };

  const onAdd = (item: Entity) => {
    if (!props.task) return;
    const newTask = props.task.clone();

    switch (props.field.fieldType) {
      case TaskTypeFieldType.ContextAsset:
        const asset = assets.find((c) => c.assetId === item.entityId);
        if (asset) newTask.assetIds.push(asset.assetId);
        break;
      case TaskTypeFieldType.ContextControl:
        const control = controls.find((c) => c.controlId === item.entityId);
        if (control) newTask.controlIds.push(control.controlId);
        break;
      case TaskTypeFieldType.ContextObjective:
        const objective = objectives.find((c) => c.objectiveId === item.entityId);
        if (objective) newTask.objectiveIds.push(objective.objectiveId);
        break;
      case TaskTypeFieldType.ContextProcess:
        const process = processes.find((c) => c.processId === item.entityId);
        if (process) newTask.processIds.push(process.processId);
        break;
      case TaskTypeFieldType.ContextRequirement:
        const theme = themes.find((c) => c.themeId === item.entityId);
        if (theme) newTask.controlIds.push(theme.themeId);
        break;
      case TaskTypeFieldType.ContextRisk:
        const risk = risks.find((c) => c.riskId === item.entityId);
        if (risk) newTask.riskIds.push(risk.riskId);
        break;
      default:
        return [];
    }

    if (props.onUpdateTaskForForm) props.onUpdateTaskForForm(newTask);
  };

  const onRemove = (item: Entity) => {
    if (!props.task) return;
    const newTask = props.task.clone();

    //remove the item from the task context
    switch (props.field.fieldType) {
      case TaskTypeFieldType.ContextAsset:
        newTask.assetIds = newTask.assetIds.filter((c) => c !== item.entityId);
        break;
      case TaskTypeFieldType.ContextControl:
        newTask.controlIds = newTask.controlIds.filter((c) => c !== item.entityId);
        break;
      case TaskTypeFieldType.ContextObjective:
        newTask.objectiveIds = newTask.objectiveIds.filter((c) => c !== item.entityId);
        break;
      case TaskTypeFieldType.ContextProcess:
        newTask.processIds = newTask.processIds.filter((c) => c !== item.entityId);
        break;
      case TaskTypeFieldType.ContextRequirement:
        newTask.controlIds = newTask.controlIds.filter((c) => c !== item.entityId);
        break;
      case TaskTypeFieldType.ContextRisk:
        newTask.riskIds = newTask.riskIds.filter((c) => c !== item.entityId);
        break;
    }

    if (props.onUpdateTaskForForm) props.onUpdateTaskForForm(newTask);
  };

  //
  // Main render
  //
  return (
    <IPStack>
      <SingleFormElementInfoField {...props} />
      <EntityTagPicker
        entityType={getEntityType()}
        tagColor={defSpecificItemTagColor}
        suggestionLimit={undefined}
        itemLimit={props.field.getMaxCount()}
        entities={getEntities()}
        selectedEntities={getSelectedEntitiesFromTask()}
        onAddEntity={onAdd}
        onRemoveEntity={onRemove}
        loadData={async () => await loadData(type)}
        isLoading={isLoading}
        disabled={props.readonly || isLoading}
        placeholder={t('forms:Dialogs.FieldForm.Fields.ContextPlaceholder')}
      />
    </IPStack>
  );
};
